import acequia from '../../acequia.js'
import SimplePeer from '../../build/simplepeer.es6.js'
import { setEncode, UnChunkerFactory } from './dataUtils.js'
import { PeerBinaryFactory } from './PeerBinary.js'
import { DEFAULT_TRANSPORT, resolveTransport } from './transport.js'

import { encode, decode } from '../../build/msgpack.es6.js'
import { wrtc as nodeWrtc } from '../../deps.js'
import { normalizeBody } from '../requestNormalize.js'
import { activeGroups } from '../acequia.js'
import { isPeerLimitError, notePeerLimit, notePeerCreated, peerLimitActive, peerLimitError } from './peerLimit.js'

// const { debug } = acequia.config
let debug = false

// Cloudflare STUN — used both as the config-less default (overriding simple-peer's
// built-in Google + Twilio STUN default, so we never reference those third-party
// servers) and as the fallback when /turn/credentials can't be fetched. Real peers
// pass full Cloudflare STUN+TURN via getIceServers(); this is only the safety net.
//
// Port 3478 only. The port-53 alternative was here until 2026-09-28: with it
// anywhere in Chrome's list, a Chrome peer gathered two candidates instead
// of four and never connected to a Node peer (measured against
// node-datachannel and werift alike, as one entry or two, alone or beside
// 3478); the production TURN credentials never carried it. Node filters
// it from any list it is handed (nodeSafe) as well.
const DEFAULT_STUN = [
    { urls: ['stun:stun.cloudflare.com:3478'] },
]
SimplePeer.config = { iceServers: DEFAULT_STUN }

// Dynamic ICE credential fetching with client-side caching
let cachedIce = null
let iceOriginOverride = null
/** Node: the origin whose /turn/credentials to ask (no page to infer it from). */
export function setIceOrigin(origin) {
    iceOriginOverride = origin ? new URL(origin).origin + '/' : null
    cachedIce = null
}
let pendingIceFetch = null

export async function getIceServers() {
    if (cachedIce && cachedIce.expiresAt > Date.now()) {
        return nodeSafe(cachedIce.servers)
    }
    // Deduplicate concurrent fetches
    if (pendingIceFetch) return pendingIceFetch
    pendingIceFetch = _fetchIceServers().then(nodeSafe)
    try {
        return await pendingIceFetch
    } finally {
        pendingIceFetch = null
    }
}

/**
 * In Node (the vendored stack, deps.js `wrtc`), a STUN/TURN URL on port 53
 * in the list stops the connection to a Chrome peer from ever completing —
 * both sides gather fewer candidates and no pair succeeds (measured
 * 2026-09-28: 3478 alone connects, 53 alone or beside 3478 does not, as one
 * entry or two). A browser keeps the list as given.
 */
function nodeSafe(servers) {
    if (!nodeWrtc) return servers
    return nodeSafeIceServers(servers)
}

/** The rule itself, for tests: the list without any port-53 URL. */
export function nodeSafeIceServers(servers) {
    if (!Array.isArray(servers)) return servers
    return servers
        .map((s) => {
            const urls = (Array.isArray(s.urls) ? s.urls : [s.urls]).filter((u) => typeof u === 'string' && !/:53(\?|$)/.test(u))
            return urls.length ? { ...s, urls } : null
        })
        .filter(Boolean)
}

async function _fetchIceServers() {
    try {
        // TURN credentials live on the acequia auth server, not necessarily
        // this page's origin: on acequia-served domains authURL is relative
        // ('/auth') and this resolves same-origin, unchanged — but on
        // statically-hosted apps (alert.live) the page origin has no
        // handler, and the auth server is the one that does.
        const authURL = acequia.config?.acequia?.authURL || '/auth'
        // Outside a window there is no page to resolve a relative authURL
        // against: a Node process names its origin (setIceOrigin, from the
        // registered server's discovery URL) or gets the STUN defaults.
        const pageHref = typeof self !== 'undefined' && self.location ? self.location.href : iceOriginOverride
        if (!pageHref) throw new Error('no origin for TURN credentials outside a window')
        const turnOrigin = new URL(authURL, pageHref).origin
        const resp = await fetch(`${turnOrigin}/turn/credentials`)
        if (resp.ok) {
            const { iceServers, ttl } = await resp.json()
            cachedIce = {
                servers: iceServers,
                expiresAt: Date.now() + (ttl * 500), // refresh at half-life
            }
            console.log(`[webrtc] ICE servers: ${iceServers.map(s => Array.isArray(s.urls) ? s.urls.join(', ') : s.urls).join('; ')}`)
            // Self-test TURN relay reachability once per page load, fire-and-forget.
            // Logs whether this node can actually allocate a relay candidate — the
            // result is visible via the debug-console-receiver, so any node can be
            // checked remotely without on-site access.
            if (!turnSelfTestDone) {
                turnSelfTestDone = true
                testTurnReachability({ iceServers }).catch(() => {})
            }
            return iceServers
        }
        console.warn(`[webrtc] TURN credentials fetch returned ${resp.status}`)
    } catch (e) {
        console.warn('[webrtc] Failed to fetch TURN credentials:', e.message)
    }
    // Fallback to static config
    const fallback = acequia.config.ice || DEFAULT_STUN
    console.log(`[webrtc] Using fallback ICE: ${fallback.map(s => Array.isArray(s.urls) ? s.urls.join(', ') : s.urls).join('; ')}`)
    return fallback
}

export function invalidateIceCache() {
    cachedIce = null
}

let turnSelfTestDone = false

/**
 * Probe whether this node can actually allocate a TURN relay candidate.
 *
 * Creates a throwaway relay-only RTCPeerConnection with the given (or fetched)
 * iceServers and gathers candidates. If TURN egress is blocked at the node's
 * network/router (or credentials are rejected), zero relay candidates are
 * produced even though TURN is configured — that's the signature we hunt for.
 *
 * Logs a single `[turn-selftest] …` line (visible via debug-console-receiver)
 * and resolves with the result. Safe to call anytime.
 *
 * @param {object} [opts]
 * @param {RTCIceServer[]} [opts.iceServers] - servers to test (defaults to getIceServers())
 * @param {number} [opts.timeout=8000] - max gather time in ms
 * @returns {Promise<{hasRelay:boolean, relayCount:number, relayProtocols:string[], timedOut:boolean, error?:string}>}
 */
export async function testTurnReachability({ iceServers, timeout = 8000 } = {}) {
    const servers = iceServers || await getIceServers()
    return new Promise((resolve) => {
        let settled = false
        let pc = null
        const relay = []
        const finish = (timedOut) => {
            if (settled) return
            settled = true
            try { pc && pc.close() } catch (e) { /* ignore */ }
            const protocols = [...new Set(relay.map(c => c.relayProtocol || c.protocol).filter(Boolean))]
            const result = {
                hasRelay: relay.length > 0,
                relayCount: relay.length,
                relayProtocols: protocols,
                timedOut: !!timedOut,
            }
            console.log(`[turn-selftest] relay reachable: ${result.hasRelay} — ${result.relayCount} relay candidate(s), protocols: ${protocols.join(', ') || 'none'}${timedOut && !result.hasRelay ? ' (timed out — TURN likely blocked)' : ''}`)
            resolve(result)
        }
        try {
            pc = new RTCPeerConnection({ iceServers: servers, iceTransportPolicy: 'relay' })
        } catch (e) {
            console.warn('[turn-selftest] could not create RTCPeerConnection:', e.message)
            return resolve({ hasRelay: false, relayCount: 0, relayProtocols: [], timedOut: false, error: e.message })
        }
        pc.onicecandidate = (e) => {
            if (e.candidate && e.candidate.candidate) {
                if (e.candidate.type === 'relay') relay.push(e.candidate)
            } else if (!e.candidate) {
                finish(false) // gathering complete
            }
        }
        pc.createDataChannel('turn-selftest')
        pc.createOffer().then(o => pc.setLocalDescription(o)).catch(() => finish(false))
        setTimeout(() => finish(true), timeout)
    })
}

setEncode(encode)

const UnChunker = UnChunkerFactory({ decode })

const PeerBinary = PeerBinaryFactory({
    Peer: SimplePeer,
    UnChunker,
})

// --- Deprecated global APIs (kept for backwards compat) ---

let getPeersWarned = false
export function getPeers() {
    if (!getPeersWarned) {
        console.warn('[acequia] getPeers() is deprecated — use group.getPeerByInstanceId(id) instead')
        getPeersWarned = true
    }
    const merged = {}
    for (const [, group] of activeGroups) {
        if (group._peers) Object.assign(merged, group._peers)
    }
    return merged
}

let newPeerListeners = []

let onNewPeerWarned = false
export function onNewPeer(handler) {
    if (!onNewPeerWarned) {
        console.warn('[acequia] onNewPeer() is deprecated — use group.on(\'peerConnected\', callback) instead')
        onNewPeerWarned = true
    }
    if (debug) console.log('registering new listener: ', handler)
    newPeerListeners.push(handler)
}

export function offNewPeer(handler) {
    const idx = newPeerListeners.indexOf(handler)
    if (idx !== -1) newPeerListeners.splice(idx, 1)
}

// Called by Group after creating a peer — fires deprecated global callbacks
export function _publishNewPeer(peer) {
    for (let handler of newPeerListeners) {
        handler(peer)
    }
}

// --- Peer factory ---

/**
 * Creates a new PeerBinary connection. Does NOT store it — the calling
 * Group is responsible for storing the peer in its own _peers map.
 *
 * @param {object} opts - { peerId, group, initiator, trickle, iceServers, ... }
 * @returns {PeerBinary} The created peer (not yet connected)
 */
export function createPeer(opts) {
    let { peerId, group } = opts

    if (debug) console.log('create peer: ', peerId, group, opts)

    if (!peerId) {
        throw new Error('peerId is a required parameter in options')
    }

    if (!group) {
        throw new Error('Group is a required parameter in options')
    }

    // Past the browser's cap, creation pauses for the whole page (peerLimit.js)
    if (peerLimitActive()) throw peerLimitError(peerId)

    // In Node the peer-connection classes come from the vendored stack
    // (deps.js, loaded at initRuntime); a browser leaves this null and
    // simple-peer uses the globals.
    if (nodeWrtc && !opts.wrtc) opts = { ...opts, wrtc: nodeWrtc }
    const peer = new PeerBinary(opts)
    // simple-peer made its RTCPeerConnection, or failed to and will emit the error
    if (peer._pc) notePeerCreated()
    peer.group = group
    peer.peerId = peerId
    peer._createdAt = Date.now()
    // The groups check peer._destroyed; simple-peer sets destroyed. Nothing
    // set _destroyed, so every such check read "not destroyed".
    Object.defineProperty(peer, '_destroyed', { get() { return !!this.destroyed }, configurable: true })
    // Shared across monitorIce's failed-state detection and the 'error' handler
    // so iceFailure is emitted at most once per peer regardless of which path
    // observes the failure first.
    peer._iceFailureFired = false

    const shortId = peerId.slice(0, 8)
    const role = opts.initiator ? 'initiator' : 'responder'
    if (debug) console.log(`[webrtc] creating peer ${shortId} as ${role}`)

    peer.on('signal', (data) => {
        if (debug) console.log('SIGNAL', JSON.stringify(data))
        // The signalling and ICE trace is debug-only (config.debug): a busy page
        // logged a dozen lines per peer, which filled the debug console's buffer
        // in minutes and hid what mattered (owner, 2026-10-21)
        if (!debug) {
            // nothing to say
        } else if (data.type === 'offer' || data.type === 'answer') {
            console.log(`[webrtc] ${shortId} signaling: ${data.type}`)
        } else if (data.candidate) {
            const c = typeof data.candidate === 'string' ? data.candidate : data.candidate?.candidate || ''
            const proto = c.match(/udp|tcp/i)?.[0] || '?'
            const typ = c.match(/typ\s+(\w+)/)?.[1] || '?'
            console.log(`[webrtc] ${shortId} ICE candidate: ${proto} ${typ}`)
        }
        peer?.group?.serverSendToPeer(peerId, { type: 'signal', data })
    })

    // Monitor ICE connection state changes on the underlying RTCPeerConnection
    let monitorTries = 0
    const monitorIce = () => {
        const pc = peer._pc
        if (!pc) {
            // SimplePeer hasn't created the RTCPeerConnection yet: look again
            // shortly. A peer whose connection was never made (the browser
            // refused it) is destroyed: stop. This polled every 100 ms for good
            // and kept the dead peer alive, one loop per refusal.
            if (peer.destroyed || ++monitorTries > 50) return
            setTimeout(monitorIce, 100)
            return
        }
        let iceDisconnectedTimer = null
        const emitIceFailure = () => {
            if (peer._iceFailureFired) return
            peer._iceFailureFired = true
            if (iceDisconnectedTimer) { clearTimeout(iceDisconnectedTimer); iceDisconnectedTimer = null }
            peer.emit('iceFailure')
        }

        pc.addEventListener('iceconnectionstatechange', () => {
            if (debug) console.log(`[webrtc] ${shortId} ICE state: ${pc.iceConnectionState}`)
            if (pc.iceConnectionState === 'failed') {
                console.warn(`[webrtc] ${shortId} ICE failed — requesting reconnect`)
                emitIceFailure()
            } else if (pc.iceConnectionState === 'disconnected') {
                // Grace period — transient disconnects often self-recover
                if (!iceDisconnectedTimer) {
                    console.log(`[webrtc] ${shortId} ICE disconnected — waiting 5s before acting`)
                    iceDisconnectedTimer = setTimeout(() => {
                        iceDisconnectedTimer = null
                        if (pc.iceConnectionState === 'disconnected' || pc.iceConnectionState === 'failed') {
                            console.warn(`[webrtc] ${shortId} ICE still disconnected after 5s — treating as failure`)
                            emitIceFailure()
                        }
                    }, 5000)
                }
            } else if (pc.iceConnectionState === 'connected' || pc.iceConnectionState === 'completed') {
                // Recovered — cancel any pending disconnected timer
                if (iceDisconnectedTimer) {
                    console.log(`[webrtc] ${shortId} ICE recovered from disconnected`)
                    clearTimeout(iceDisconnectedTimer)
                    iceDisconnectedTimer = null
                }
            }
        })
        pc.addEventListener('icegatheringstatechange', () => {
            if (debug) console.log(`[webrtc] ${shortId} ICE gathering: ${pc.iceGatheringState}`)
        })
        pc.addEventListener('connectionstatechange', () => {
            if (debug) console.log(`[webrtc] ${shortId} connection state: ${pc.connectionState}`)
            if (pc.connectionState === 'failed') {
                console.warn(`[webrtc] ${shortId} connection failed — requesting reconnect`)
                emitIceFailure()
            }
        })
    }
    monitorIce()

    peer.on('connect', () => {
        console.log(`[webrtc] ${shortId} connected (${Date.now() - peer._createdAt}ms)`)
    })
    peer.on('close', () => {
        console.log(`[webrtc] ${shortId} closed (age ${Date.now() - peer._createdAt}ms)`)
        // Half-assembled request streams die with the channel — error any
        // live bodyStreams so their handlers fail instead of hanging.
        if (peer._incomingRequestStreams) {
            for (const entry of peer._incomingRequestStreams.values()) {
                if (entry.live) entry.controller?.error(new Error('peer closed mid stream'))
            }
            peer._incomingRequestStreams.clear()
        }
        // Notify the owning group to clean up its _peers map
        if (peer.group && peer.group._peers && peer.group._peers[peerId] === peer) {
            delete peer.group._peers[peerId]
        }
    })
    peer.on('error', (err) => {
        // The browser's cap: pause creation page-wide and use the relay, with
        // no ICE restarts (each would be refused too) and one log per pause
        if (isPeerLimitError(err)) {
            notePeerLimit()
            if (peer.group && peer.group._peers && peer.group._peers[peerId] === peer) {
                delete peer.group._peers[peerId]
            }
            peer._iceFailureFired = true
            peer.group?._onPeerLimit?.(peerId)
            peer.destroy()
            return
        }
        console.warn(`[webrtc] peer ${shortId} error:`, err.message || err)
        if (peer.group && peer.group._peers && peer.group._peers[peerId] === peer) {
            delete peer.group._peers[peerId]
        }
        // Route into the recovery path (ICE-restart retries → WS-relay promotion).
        // simple-peer's "Connection failed" error otherwise wins the race against
        // monitorIce's failed-state detection, destroying the peer before iceFailure
        // ever fires — so the peer would be silently dropped with no retry.
        // Deduped via peer._iceFailureFired so we never double-fire with monitorIce.
        if (!peer._iceFailureFired) {
            peer._iceFailureFired = true
            peer.emit('iceFailure')
        }
        peer.destroy()
    })

    // Handle incoming proxied requests and ping/pong from remote peers
    peer.on('dataBig', async (data) => {
        if (debug) console.log('peer received dataBig:', data.type)
        if (data.type === 'peerProxiedRequest') {
            if (debug)
                console.log('handling peerProxiedRequest:', data.requestId)
            await handlePeerProxiedRequest(data, peer)
        } else if (data.type === 'peerRequestStream') {
            await handlePeerRequestStream(data, peer)
        } else if (data.type === 'peer:ping') {
            const pong = {
                type: 'peer:pong',
                pingId: data.pingId,
                timestamp: Date.now(),
            }
            if (data.requestStatus) {
                // Report connected peers from this peer's owning group
                const groupPeers = peer.group?._peers || {}
                const connectedPeers = Object.keys(groupPeers).filter(
                    id => groupPeers[id] && !groupPeers[id]._destroyed &&
                          groupPeers[id]._channel?.readyState === 'open'
                )
                pong.status = {
                    peerId,
                    connectedPeers,
                    uptime: Date.now() - peer._createdAt,
                }
            }
            peer.sendBig(pong)
        }
    })

    // Ping/pong — send a ping and await the pong response
    peer.sendPing = (options = {}) => {
        const pingId = Math.random().toString(36).slice(2)
        const sent = Date.now()

        return new Promise((resolve, reject) => {
            const timeout = setTimeout(() => {
                peer.off('dataBig', handler)
                reject(new Error('Ping timeout'))
            }, options.timeout || 5000)

            const handler = (data) => {
                if (data.type === 'peer:pong' && data.pingId === pingId) {
                    clearTimeout(timeout)
                    peer.off('dataBig', handler)
                    resolve({
                        rtt: Date.now() - sent,
                        remoteTimestamp: data.timestamp,
                        status: data.status || null,
                    })
                }
            }
            peer.on('dataBig', handler)

            peer.sendBig({
                type: 'peer:ping',
                pingId,
                timestamp: sent,
                requestStatus: !!options.requestStatus,
            })
        })
    }

    return peer
}

// Deprecated — use group.disconnectPeer(peerId) instead
export function removePeer(peerId) {
    if (debug) console.log('remove peer: ', peerId)
    // Search all groups for this peer
    for (const [, group] of activeGroups) {
        const peer = group._peers?.[peerId]
        if (peer) {
            peer.destroy()
            delete group._peers[peerId]
            return
        }
    }
}

// Message-level streams over the data channel
// (browserdav-transport-robustness): peerRequestStream /
// peerResponseStream messages share a streamId; each 512 KB chunk is its
// own sendBig payload, so peak wire memory is one chunk and sendBig's
// per-payload timeout never applies to the whole file.
const PEER_STREAM_CHUNK = 512 * 1024
// The stream gate — bodies past this size stream instead of riding one
// msgpack payload — is per-peer transport config (webrtc-transfer-tuning).
const streamThresholdFor = (peer) =>
    peer?.transport?.streamThreshold ?? DEFAULT_TRANSPORT.streamThreshold

/**
 * Handle one peerRequestStream message. Routes registered with
 * `metadata.streaming` are dispatched at 'start' with a live
 * `bodyStream` (ReadableStream fed by incoming chunks); other routes
 * buffer and dispatch on 'end'.
 */
async function handlePeerRequestStream(data, peer) {
    if (!peer._incomingRequestStreams) peer._incomingRequestStreams = new Map()
    const streams = peer._incomingRequestStreams
    const { kind, streamId } = data

    if (kind === 'start') {
        const { kind: _k, seq: _s, streamId: _i, type: _t, ...meta } = data
        const group = (data.groupId && activeGroups.get(data.groupId)) || peer.group
        let streamingMatch = null
        try {
            const match = group?.router?.matchRoute(data.path)
            if (match?.metadata?.streaming) streamingMatch = match
        } catch {
            // no route — buffered path will produce the 404 on 'end'
        }
        if (streamingMatch) {
            const entry = { meta, seq: data.seq, live: true }
            entry.bodyStream = new ReadableStream({
                start(controller) {
                    entry.controller = controller
                },
            })
            streams.set(streamId, entry)
            dispatchPeerRequest({ ...meta, type: 'peerProxiedRequest' }, peer, {
                bodyStream: entry.bodyStream,
            }).catch((e) =>
                console.warn('[stream] peer streamed dispatch failed:', e?.message || e)
            )
            return
        }
        streams.set(streamId, { meta, seq: data.seq, chunks: [] })
        return
    }

    const entry = streams.get(streamId)
    if (!entry) {
        console.warn(`[stream] peer frame for unknown stream ${streamId} (kind=${kind}) — dropped`)
        return
    }
    if (kind === 'abort') {
        streams.delete(streamId)
        if (entry.live) entry.controller?.error(new Error(data.error || 'aborted by sender'))
        return
    }
    if (data.seq !== entry.seq + 1) {
        console.warn(`[stream] peer seq gap on ${streamId} (got ${data.seq}, expected ${entry.seq + 1}) — aborting`)
        streams.delete(streamId)
        if (entry.live) {
            entry.controller?.error(new Error('stream sequence error'))
        } else {
            peer.sendBig({
                type: 'peerProxiedResponse',
                requestId: entry.meta.requestId,
                response: { data: { status: 400, body: 'stream sequence error', headers: { 'content-type': 'text/plain' } } },
            })
        }
        return
    }
    entry.seq = data.seq

    if (kind === 'data') {
        const chunk = data.chunk instanceof Uint8Array ? data.chunk : new Uint8Array(data.chunk)
        if (entry.live) entry.controller?.enqueue(chunk)
        else entry.chunks.push(chunk)
        return
    }
    if (kind === 'end') {
        streams.delete(streamId)
        if (entry.live) {
            entry.controller?.close()
            return
        }
        await dispatchPeerRequest({ ...entry.meta, type: 'peerProxiedRequest' }, peer, {
            bufferedBody: new Blob(entry.chunks),
        })
    }
}

/** Send a handler response as peerResponseStream messages (start/data/end). */
async function sendPeerResponseStream(peer, requestId, responseData) {
    const meta = { ...responseData, body: undefined }
    let seq = 0
    // sendBig resolves false when the channel closed or a drain/send timeout
    // fired. That must abort the stream — otherwise the requester only ever
    // sees a seq gap or its own idle timeout (webrtc-transfer-tuning).
    const put = async (msg) => {
        if ((await peer.sendBig(msg)) === false) {
            throw new Error(`sendBig failed (kind=${msg.kind}, seq=${msg.seq})`)
        }
    }
    await put({
        type: 'peerResponseStream',
        kind: 'start',
        streamId: requestId,
        seq,
        response: { data: meta },
    })
    const body = responseData.body
    const reader = (body instanceof Blob ? body.stream() : body).getReader()
    let total = 0
    let pending = []
    let pendingBytes = 0
    const flush = async () => {
        if (!pendingBytes) return
        let chunk
        if (pending.length === 1) {
            chunk = pending[0]
        } else {
            chunk = new Uint8Array(pendingBytes)
            let off = 0
            for (const p of pending) {
                chunk.set(p, off)
                off += p.byteLength
            }
        }
        pending = []
        pendingBytes = 0
        total += chunk.byteLength
        seq++
        await put({ type: 'peerResponseStream', kind: 'data', streamId: requestId, seq, chunk })
    }
    try {
        for (;;) {
            const { done, value } = await reader.read()
            if (done) break
            pending.push(value)
            pendingBytes += value.byteLength
            if (pendingBytes >= PEER_STREAM_CHUNK) await flush()
        }
        await flush()
        seq++
        await put({ type: 'peerResponseStream', kind: 'end', streamId: requestId, seq, totalBytes: total })
    } catch (e) {
        console.warn(`[stream] peer response stream ${requestId} failed:`, e?.message || e)
        try {
            await peer.sendBig({ type: 'peerResponseStream', kind: 'abort', streamId: requestId, error: String(e?.message || e) })
        } catch { /* channel dead */ }
        reader.cancel?.().catch?.(() => {})
    }
}

/**
 * Handle incoming proxied requests from remote peers
 * @param {object} data - The request data
 * @param {object} peer - The peer connection
 */
async function handlePeerProxiedRequest(data, peer) {
    return dispatchPeerRequest(data, peer)
}

/**
 * Shared dispatch for whole-body and streamed peer requests: find the
 * group/router, run the handler, send the response — streamed back as
 * peerResponseStream messages when the requester advertised
 * `acceptStreamedResponse` and the body is a ReadableStream or large Blob.
 */
async function dispatchPeerRequest(data, peer, { bodyStream = null, bufferedBody = null } = {}) {
    const { requestId } = data

    if (debug)
        console.log('handlePeerProxiedRequest called:', {
            requestId,
            path: data.path,
        })

    try {
        // Use groupId from the message to find the correct group's router.
        // With per-group peers, peer.group is always correct, but we keep
        // the groupId lookup for backwards compat with the route disambiguation fix.
        const group = (data.groupId && activeGroups.get(data.groupId)) || peer.group
        // Diagnostic: trace proxied request handling on receiving peer
        const routeCount = group?.router ? Object.keys(group.router.getRouteRegistry()).length : 0
        if (debug) console.log(`[route-diag] peerProxiedRequest: path=${data.path}, group=${data.groupId || peer.group?.groupId}, localRoutes=${routeCount}, from=${peer.id?.slice(0, 8)}`)
        if (group?._workerPool && data.jobType) {
            const poolResult = await group._workerPool._handleJobOffer(data, peer)
            peer.sendBig({
                type: 'peerProxiedResponse',
                requestId,
                response: poolResult,
            })
            return
        }

        // Find the group and router that can handle this request
        if (!group || !group.router) {
            console.warn(`[route-diag] peerProxiedRequest FAILED: no group/router for path=${data.path}`)
            throw new Error('No group or router available for peer')
        }

        if (debug) console.log('Matching route for:', data.path)

        // Match the route
        const match = group.router.matchRoute(data.path)
        if (!match) {
            const available = Object.keys(group.router.getRouteRegistry())
            console.warn(`[route-diag] peerProxiedRequest NO MATCH: path=${data.path}, available=[${available.join(',')}]`)
            throw new Error(`No route found for path: ${data.path}`)
        }

        if (debug) console.log(`[route-diag] peerProxiedRequest MATCHED: path=${data.path} → pattern=${match.pattern}`)

        // Execute the route handler
        // Convert Blob to ArrayBuffer before normalizing — Blobs survive
        // WebRTC round-trip (encode→descriptor→decode→Blob) but normalizeBody
        // is sync and can't handle them (JSON.stringify(blob) yields "{}").
        let body = null
        let rawBody = null
        if (!bodyStream) {
            let rawInput = bufferedBody !== null ? bufferedBody : data.body
            if (typeof Blob !== 'undefined' && rawInput instanceof Blob) {
                rawInput = await rawInput.arrayBuffer()
            }
            ;({ body, rawBody } = normalizeBody(rawInput, data.headers?.['content-type']))
        }
        // Thread the router's wildcard capture to the handler, matching the
        // other three dispatch sites (groups._dispatchProxiedRequest,
        // acequia.js local dispatch, sw.js). Without these a `*path` route
        // handler sees remainingPath undefined over the direct-peer
        // transport only — BrowserDAV's filesystem then 404s every request
        // ("Resource not found: undefined") that works over the proxy.
        // Receiver-side derivation is authoritative over anything in `data`.
        const responseData = await match.handler({
            ...data,
            body,
            rawBody,
            pathParams: match.pathParams,
            remainingPath: match.remainingPath,
            ...(bodyStream ? { bodyStream } : {}),
        })

        if (debug)
            console.log(
                'Handler executed, sending response:',
                requestId,
                responseData
            )

        // Streamed response: requester advertised it can assemble one, and
        // the body is a stream or a Blob past the threshold.
        const respBody = responseData?.body
        const streamResponse =
            data.acceptStreamedResponse === true &&
            ((typeof ReadableStream !== 'undefined' && respBody instanceof ReadableStream) ||
                (typeof Blob !== 'undefined' && respBody instanceof Blob && respBody.size > streamThresholdFor(peer)))
        if (streamResponse) {
            await sendPeerResponseStream(peer, requestId, responseData)
            return
        }
        // Legacy requester + ReadableStream body — buffer before the single
        // sendBig payload can carry it.
        if (typeof ReadableStream !== 'undefined' && respBody instanceof ReadableStream) {
            responseData.body = await new Response(respBody).blob()
        }

        // Send response back via WebRTC
        peer.sendBig({
            type: 'peerProxiedResponse',
            requestId,
            response: {
                data: responseData,
            },
        })

        if (debug) console.log('Response sent for:', requestId)
    } catch (error) {
        console.error('Error handling peer proxied request:', error)

        // Send error response back via WebRTC as JSON
        peer.sendBig({
            type: 'peerProxiedResponse',
            requestId,
            response: {
                data: {
                    status: 500,
                    statusText: 'Internal Server Error',
                    body: JSON.stringify({
                        error: error.message,
                        stack: error.stack,
                    }),
                    headers: { 'content-type': 'application/json' },
                },
            },
        })
    }
}

// Exported for tests (stream-message assembly is driven by fake peers there)
export { handlePeerRequestStream, dispatchPeerRequest, sendPeerResponseStream }
export { DEFAULT_TRANSPORT, resolveTransport }

export { PeerBinary }
